import { useEffect, useMemo, useState } from "react";
import { getAddress, isHash, type Address, type Hash } from "viem";
import { ethereumClient } from "./chain";
import { receiverAbi } from "./contracts";
import { discoverDeploymentConfig, type DeploymentConfig } from "./deployment";
import { recoverRedemption, type PendingRedemption, type Redemption } from "./redemption";
import { connectInjectedWallet, friendlyError } from "./wallet";

type Status = { kind: "idle" | "pending" | "success" | "error"; message?: string; hash?: Hash };

export function ClaimApp() {
  const query = new URLSearchParams(window.location.search);
  const [transactionHash, setTransactionHash] = useState(query.get("tx") ?? "");
  const [account, setAccount] = useState<Address>();
  const [pending, setPending] = useState<PendingRedemption>();
  const [redemption, setRedemption] = useState<Redemption>();
  const [processed, setProcessed] = useState(false);
  const [config, setConfig] = useState<DeploymentConfig>({});
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const shortAccount = useMemo(
    () => account ? `${account.slice(0, 6)}…${account.slice(-4)}` : "Connect wallet",
    [account],
  );

  useEffect(() => {
    if (!window.ethereum) return;
    window.ethereum.request({ method: "eth_accounts" }).then((accounts) => {
      const first = (accounts as string[])[0];
      if (first) setAccount(getAddress(first));
    });
  }, []);

  useEffect(() => {
    if (isHash(transactionHash)) lookup(transactionHash).catch(() => undefined);
    // Only auto-run for the initial URL value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function connect() {
    try {
      setAccount(await connectInjectedWallet());
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  async function requireDeployment() {
    const next = await discoverDeploymentConfig({ requireActivation: true });
    setConfig(next);
    if (!next.burnerAddress || !next.receiverAddress) {
      throw new Error("The independent contracts have not been activated yet.");
    }
    return { ...next, burnerAddress: next.burnerAddress, receiverAddress: next.receiverAddress };
  }

  async function lookup(hashValue?: string) {
    const hash = hashValue ?? transactionHash;
    if (!isHash(hash)) {
      setStatus({ kind: "error", message: "Enter a complete Base transaction hash." });
      return;
    }
    try {
      setStatus({ kind: "pending", message: "Verifying the Base burn…" });
      const active = await requireDeployment();
      const recovered = await recoverRedemption(hash, active.burnerAddress);
      setPending(recovered.pending);
      setRedemption(recovered.redemption);
      localStorage.setItem("hardware:pending-redemption", JSON.stringify(recovered.pending));
      const alreadyProcessed = await ethereumClient.readContract({
        address: active.receiverAddress,
        abi: receiverAbi,
        functionName: "processed",
        args: [recovered.redemption.redemptionId],
      });
      setProcessed(alreadyProcessed);
      setStatus({
        kind: alreadyProcessed ? "success" : "idle",
        message: alreadyProcessed ? "This redemption has already been minted on Ethereum." : undefined,
      });
    } catch (error) {
      setStatus({ kind: "error", message: friendlyError(error) });
    }
  }

  return (
    <main className="claim-page">
      <section className="claim-card">
        <a className="back-link" href="/">← Hardware</a>
        <img className="mark" src="/mark.png" alt="Jack Butcher" />
        <p className="eyebrow">Hardware claim</p>
        <h1>Claim your Ethereum token.</h1>
        <p className="claim-copy">Enter the Base transaction where your 1988 tokens were burned.</p>

        <label className="field-label" htmlFor="claim-hash">Base transaction hash</label>
        <input
          id="claim-hash"
          className="wide-input"
          value={transactionHash}
          onChange={(event) => setTransactionHash(event.target.value.trim())}
          placeholder="0x…"
        />
        <button className="primary" onClick={() => lookup()}>Verify burn</button>

        {redemption && (
          <div className="claim-proof">
            <span>Recipient</span><strong>{redemption.recipient}</strong>
            <span>1988 burned</span><strong>{redemption.burnAmount.toString()}</strong>
            <span>Redemption</span><strong>#{redemption.redemptionNumber.toString()}</strong>
          </div>
        )}

        {pending && !processed && (
          <div className="ledger-note">
            <strong>Burn verified.</strong>
            <p>This claim is ready for the owner’s Ledger approval. You do not need to burn again.</p>
          </div>
        )}

        <button className="wallet claim-wallet" onClick={connect}>{shortAccount}</button>
        {status.message && (
          <div className={`transaction ${status.kind}`}>
            {status.message}
            {status.hash && <a href={`https://etherscan.io/tx/${status.hash}`} target="_blank" rel="noreferrer">View transaction ↗</a>}
          </div>
        )}
      </section>
      <aside className="claim-art" aria-hidden="true" />
    </main>
  );
}
